import React, { useState, useEffect, useRef } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebaseConfig';
import '../style/ErrorReportsPage.css';
import { FaChevronLeft, FaChevronRight, FaSearch, FaSortUp, FaSortDown, FaExclamationTriangle, FaFilter, FaEye, FaDownload, FaTimes, FaUser, FaIdCard, FaExclamationCircle, FaCheckCircle, FaClock, FaBan } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useUserProfile } from '../contexts/UserProfileContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ErrorReportsPage = () => {
  const { isDarkMode } = useTheme();
  const { userProfile } = useUserProfile();
  const [errorReports, setErrorReports] = useState([]);
  const [selectedErrorReport, setSelectedErrorReport] = useState(null);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [detailModalShow, setDetailModalShow] = useState(false);
  const tableContainerRef = useRef(null);
  const filtersRef = useRef(null);

  const [searchTerms, setSearchTerms] = useState({
    errorSummary: '',
    uid: '',
    userName: ''
  });

  const [sortOrder, setSortOrder] = useState('desc');
  const [showSearch, setShowSearch] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [generalSearchTerm, setGeneralSearchTerm] = useState('');
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const [filters, setFilters] = useState({});
  const [visibleColumns, setVisibleColumns] = useState({
    errorSummary: true,
    screenshotUrl: true,
    timestamp: true,
    uid: true,
    userName: true,
    status: true,
    deviceInfo: false,
    browserInfo: false,
    errorDetails: false
  });

  useEffect(() => {
    const fetchErrorReports = async () => {
      try {
        const errorReportsCollection = collection(db, 'ErrorFeedbacks');
        const errorReportsSnapshot = await getDocs(errorReportsCollection);

        const reports = await Promise.all(
          errorReportsSnapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            
            let userName = '';
            let userFound = false;
            
            // First check if userName is already in the error report
            if (data.userName && data.userName.trim()) {
              userName = data.userName.trim();
              userFound = true;
            }
            
            // If not found, try to fetch from Users/Employees
            if (!userFound) {
              try {
                // Try Users collection
                const userDocRef = doc(db, 'Users', data.uid);
                const userDocSnapshot = await getDoc(userDocRef);
                
                if (userDocSnapshot.exists()) {
                  const userData = userDocSnapshot.data();
                  
                  const firstName = userData.firstName || userData.name || userData.eName || 
                                   userData.first_name || userData.givenName || '';
                  const lastName = userData.lastName || userData.surname || userData.eSurname || 
                                  userData.last_name || userData.familyName || '';
                  const fullName = userData.displayName || userData.fullName || userData.username || '';
                  
                  userName = fullName.trim() || `${firstName} ${lastName}`.trim();
                  
                  if (userName) {
                    userFound = true;
                  }
                }
                
                // If not found in Users or name is empty, try Employees collection
                if (!userFound) {
                  const employeeDocRef = doc(db, 'Employees', data.uid);
                  const employeeDocSnapshot = await getDoc(employeeDocRef);
                  
                  if (employeeDocSnapshot.exists()) {
                    const employeeData = employeeDocSnapshot.data();
                    
                    const eName = employeeData.eName || employeeData.name || employeeData.firstName || 
                                 employeeData.first_name || '';
                    const eSurname = employeeData.eSurname || employeeData.surname || employeeData.lastName || 
                                    employeeData.last_name || '';
                    const fullName = employeeData.displayName || employeeData.fullName || '';
                    
                    userName = fullName.trim() || `${eName} ${eSurname}`.trim();
                    
                    if (userName) {
                      userFound = true;
                    }
                  }
                }
                
                // If still not found, use UID as fallback
                if (!userFound || !userName) {
                  userName = `Eski Kullanıcı (${data.uid.substring(0, 8)}...)`;
                }
              } catch (error) {
                console.error(`Error fetching user data for uid ${data.uid}:`, error);
                userName = `Eski Kullanıcı (${data.uid.substring(0, 8)}...)`;
              }
            }
            
            return {
              id: docSnapshot.id,
              ...data,
              userName: userName,
              status: data.status || 'Beklemede',
              statusUpdatedBy: data.statusUpdatedBy || null,
              statusUpdatedAt: data.statusUpdatedAt || null,
              statusUpdatedByName: data.statusUpdatedByName || null,
              deviceInfo: data.deviceInfo || 'Unknown Device',
              browserInfo: data.browserInfo || 'Unknown Browser',
              errorDetails: data.errorDetails || 'No detailed information'
            };
          })
        );
        setErrorReports(reports);
      } catch (error) {
        console.error('Error fetching error reports: ', error);
        toast.error('Hata raporları alınırken sorun oluştu');
      }
    };

    fetchErrorReports();
    
    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  const handleViewScreenshot = (screenshotUrl) => {
    setSelectedScreenshot(screenshotUrl);
    setModalShow(true);
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const filteredReports = errorReports
    .filter((report) => {
      // Apply general search first
      if (generalSearchTerm) {
        const searchLower = generalSearchTerm.toLowerCase();
        return (
          (report.errorSummary && report.errorSummary.toLowerCase().includes(searchLower)) ||
          (report.uid && report.uid.toLowerCase().includes(searchLower)) ||
          (report.userName && report.userName.toLowerCase().includes(searchLower))
        );
      }
      
      // Then apply specific filters
      return (
        (!searchTerms.errorSummary || 
          (report.errorSummary && report.errorSummary.toLowerCase().includes(searchTerms.errorSummary.toLowerCase()))) &&
        (!searchTerms.uid || 
          (report.uid && report.uid.toLowerCase().includes(searchTerms.uid.toLowerCase()))) &&
        (!searchTerms.userName || 
          (report.userName && report.userName.toLowerCase().includes(searchTerms.userName.toLowerCase())))
      );
    })
    .sort((a, b) => {
      if (sortOrder === 'asc') {
        return new Date(a.timestamp) - new Date(b.timestamp);
      } else {
        return new Date(b.timestamp) - new Date(a.timestamp);
      }
    });

  const handleSearchChange = (e, column) => {
    setSearchTerms({
      ...searchTerms,
      [column]: e.target.value,
    });
  };

  const toggleSearch = (column) => {
    setShowSearch((prevShowSearch) => ({
      ...prevShowSearch,
      [column]: !prevShowSearch[column],
    }));
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const clearAllFilters = () => {
    setSearchTerms({
      errorSummary: '',
      uid: '',
      userName: ''
    });
    setGeneralSearchTerm('');
    setFilters({});
  };

  const handleScroll = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + 
        (direction === 'right' ? scrollAmount : -scrollAmount);
      
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll(); // Initial check
      
      container.addEventListener('scroll', handleScroll);
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [errorReports, filteredReports]);

  const handleSelectReport = (report) => {
    setSelectedErrorReport(report);
  };

  const handleStatusChange = async (reportId, newStatus) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        toast.error('Kullanıcı girişi yapılmamış');
        return;
      }

      // Get current user's name - use userProfile context first if available
      let userName = '';
      
      // First try to use the userProfile context (most reliable)
      if (userProfile && userProfile.eName && userProfile.eSurname) {
        userName = `${userProfile.eName} ${userProfile.eSurname}`.trim();
      } else if (userProfile && userProfile.displayName) {
        userName = userProfile.displayName.trim();
      }
      
      // If not available from context, fetch from database
      if (!userName) {
        try {
          // Try Users collection first
          const userDocRef = doc(db, 'Users', currentUser.uid);
          const userDocSnapshot = await getDoc(userDocRef);
          
          if (userDocSnapshot.exists()) {
            const userData = userDocSnapshot.data();
            
            // Try all possible name field combinations
            if (userData.displayName && userData.displayName.trim()) {
              userName = userData.displayName.trim();
            } else if (userData.fullName && userData.fullName.trim()) {
              userName = userData.fullName.trim();
            } else if (userData.username && userData.username.trim()) {
              userName = userData.username.trim();
            } else {
              const firstName = userData.firstName || userData.name || userData.eName || 
                               userData.first_name || userData.givenName || '';
              const lastName = userData.lastName || userData.surname || userData.eSurname || 
                              userData.last_name || userData.familyName || '';
              
              if (firstName || lastName) {
                userName = `${firstName} ${lastName}`.trim();
              }
            }
          }
          
          // If not found in Users, try Employees collection
          if (!userName) {
            const employeeDocRef = doc(db, 'Employees', currentUser.uid);
            const employeeDocSnapshot = await getDoc(employeeDocRef);
            
            if (employeeDocSnapshot.exists()) {
              const employeeData = employeeDocSnapshot.data();
              
              // Try all possible name field combinations
              if (employeeData.displayName && employeeData.displayName.trim()) {
                userName = employeeData.displayName.trim();
              } else if (employeeData.fullName && employeeData.fullName.trim()) {
                userName = employeeData.fullName.trim();
              } else {
                const eName = employeeData.eName || employeeData.name || employeeData.firstName || 
                             employeeData.first_name || '';
                const eSurname = employeeData.eSurname || employeeData.surname || employeeData.lastName || 
                                employeeData.last_name || '';
                
                if (eName || eSurname) {
                  userName = `${eName} ${eSurname}`.trim();
                }
              }
            }
          }
          
          // Final fallback
          if (!userName) {
            userName = currentUser.displayName || currentUser.email || `Kullanıcı (${currentUser.uid.substring(0, 8)}...)`;
          }
        } catch (error) {
          console.error('Error fetching user name:', error);
          userName = currentUser.displayName || currentUser.email || `Kullanıcı (${currentUser.uid.substring(0, 8)}...)`;
        }
      }

      const reportRef = doc(db, 'ErrorFeedbacks', reportId);
      const updateData = {
        status: newStatus,
        statusUpdatedBy: currentUser.uid,
        statusUpdatedByName: userName,
        statusUpdatedAt: new Date().toISOString()
      };

      await updateDoc(reportRef, updateData);

      // Update local state
      setErrorReports(prevReports =>
        prevReports.map(report =>
          report.id === reportId
            ? { ...report, ...updateData }
            : report
        )
      );

      // Update selected report if it's the one being updated
      if (selectedErrorReport && selectedErrorReport.id === reportId) {
        setSelectedErrorReport(prev => ({ ...prev, ...updateData }));
      }

      toast.success('Durum başarıyla güncellendi');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Durum güncellenirken hata oluştu');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Tamamlandı':
        return <FaCheckCircle className="status-icon completed" />;
      case 'İşlemde':
        return <FaClock className="status-icon in-progress" />;
      case 'İptal':
        return <FaBan className="status-icon cancelled" />;
      default:
        return <FaClock className="status-icon pending" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Tamamlandı':
        return 'status-completed';
      case 'İşlemde':
        return 'status-in-progress';
      case 'İptal':
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  };

  const viewErrorDetails = () => {
    if (selectedErrorReport) {
      setDetailModalShow(true);
    }
  };

  const downloadExcel = async () => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Error Reports');
      
      worksheet.columns = [
        { header: 'Error Summary', key: 'errorSummary', width: 30 },
        { header: 'Date', key: 'timestamp', width: 20 },
        { header: 'User ID', key: 'uid', width: 30 },
        { header: 'User Name', key: 'userName', width: 20 },
        { header: 'Status', key: 'status', width: 15 },
        { header: 'Status Updated By', key: 'statusUpdatedByName', width: 20 },
        { header: 'Has Screenshot', key: 'hasScreenshot', width: 15 },
        { header: 'Device Info', key: 'deviceInfo', width: 20 },
        { header: 'Browser Info', key: 'browserInfo', width: 20 },
      ];
      
      filteredReports.forEach(report => {
        worksheet.addRow({
          errorSummary: report.errorSummary || 'N/A',
          timestamp: new Date(report.timestamp).toLocaleString(),
          uid: report.uid || 'N/A',
          userName: report.userName || 'N/A',
          status: report.status || 'Beklemede',
          statusUpdatedByName: report.statusUpdatedByName || 'N/A',
          hasScreenshot: report.screenshotUrl ? 'Yes' : 'No',
          deviceInfo: report.deviceInfo || 'N/A',
          browserInfo: report.browserInfo || 'N/A',
        });
      });
      
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFE5E7EB' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF111827' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });
      
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'error_reports.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Error reports successfully downloaded');
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error('Error downloading file');
    }
  };

  // Statistics for cards
  const totalReports = errorReports.length;
  const reportsWithScreenshots = errorReports.filter(report => report.screenshotUrl).length;
  const last24HoursReports = errorReports.filter(report => {
    const reportTime = new Date(report.timestamp).getTime();
    const yesterday = new Date().getTime() - (24 * 60 * 60 * 1000);
    return reportTime > yesterday;
  }).length;

  return (
    <div className={`error-reports-container ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="error-reports-page-container">
        <header className="error-reports-header">
          <div className="error-reports-header-content">
            <h1 className="error-reports-title">
              <FaExclamationTriangle className="header-icon" />
              Hata Raporları
            </h1>
            <div className="header-buttons">
              <button className="download-button" onClick={downloadExcel}>
                <FaDownload className="button-icon" /> Tabloyu İndir
              </button>
            </div>
          </div>
        </header>

        <div className="dashboard">
          <div className="stats-cards">
            <div className="card">
              <div className="card-content">
                <div className="card-icon">
                  <FaExclamationCircle />
                </div>
                <div className="card-info">
                  <p className="card-value">{totalReports}</p>
                  <h3 className="card-title">Toplam Rapor</h3>
                </div>
              </div>
            </div>
            
            <div className="card active-card">
              <div className="card-content">
                <div className="card-icon">
                  <FaEye />
                </div>
                <div className="card-info">
                  <p className="card-value">{reportsWithScreenshots}</p>
                  <h3 className="card-title">Ekran Görüntülü</h3>
                </div>
              </div>
            </div>
            
            <div className="card alert-card">
              <div className="card-content">
                <div className="card-icon">
                  <FaExclamationTriangle />
                </div>
                <div className="card-info">
                  <p className="card-value">{last24HoursReports}</p>
                  <h3 className="card-title">Son 24 Saat</h3>
                </div>
              </div>
            </div>
          </div>

          <div className="search-filter-container">
            <div className="search-bar">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Hata açıklaması, kullanıcı ID veya isim ile ara..."
                value={generalSearchTerm}
                onChange={(e) => setGeneralSearchTerm(e.target.value)}
                className="search-input"
              />
              {generalSearchTerm && (
                <button 
                  className="clear-search" 
                  onClick={() => setGeneralSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
            </div>
            
            <div className="filter-container" ref={filtersRef}>
              <button 
                className="filter-button"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className="filter-icon" />
                <span>Filtreler</span>
                {Object.values(searchTerms).some(term => term !== '') && (
                  <span className="filter-badge">
                    {Object.values(searchTerms).filter(term => term !== '').length}
                  </span>
                )}
              </button>
              
              {showFilters && (
                <div className="filter-dropdown">
                  <div className="filter-header">
                    <h3>Filtreler</h3>
                    <button 
                      className="clear-filters-btn"
                      onClick={clearAllFilters}
                    >
                      Tümünü Temizle
                    </button>
                  </div>
                  
                  <div className="column-visibility">
                    <h4>Sütun Görünürlüğü</h4>
                    <div className="column-checkboxes">
                      {Object.keys(visibleColumns).map(column => (
                        <div key={column} className="column-checkbox">
                          <input
                            type="checkbox"
                            id={`col-${column}`}
                            checked={visibleColumns[column]}
                            onChange={() => toggleColumnVisibility(column)}
                          />
                          <label htmlFor={`col-${column}`}>
                            {column === 'errorSummary' ? 'Hata Açıklaması' :
                             column === 'screenshotUrl' ? 'Ekran Görüntüsü' :
                             column === 'timestamp' ? 'Tarih' :
                             column === 'uid' ? 'Kullanıcı ID' :
                             column === 'userName' ? 'Kullanıcı Adı' :
                             column === 'status' ? 'Durum' :
                             column === 'deviceInfo' ? 'Cihaz Bilgisi' :
                             column === 'browserInfo' ? 'Tarayıcı Bilgisi' :
                             column === 'errorDetails' ? 'Hata Detayları' : column}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedErrorReport && (
          <div className="action-panel">
            <div className="selected-error-info">
              <h3 className="selected-title">
                {selectedErrorReport.errorSummary || 'No Error Summary'}
              </h3>
              <span className="report-date">
                {new Date(selectedErrorReport.timestamp).toLocaleString()}
              </span>
            </div>
            
            <div className="action-buttons">
              {selectedErrorReport.screenshotUrl && (
                <button 
                  className="action-button view-screenshot-button" 
                  onClick={() => handleViewScreenshot(selectedErrorReport.screenshotUrl)}
                >
                  <FaEye className="action-icon" />
                  <span>Ekran Görüntüsü</span>
                </button>
              )}
              
              <button 
                className="action-button view-details-button" 
                onClick={viewErrorDetails}
              >
                <FaExclamationCircle className="action-icon" />
                <span>Detayları Gör</span>
              </button>
              
              <button className="action-button user-info-button">
                <FaUser className="action-icon" />
                <span>Kullanıcı: {selectedErrorReport.userName}</span>
              </button>
              
              <button className="action-button user-id-button">
                <FaIdCard className="action-icon" />
                <span>ID: {selectedErrorReport.uid}</span>
              </button>
            </div>
          </div>
        )}

        <section className="table-section">
          <div className="table-wrapper">
            <div className="table-scroll-container">
              {showLeftScroll && (
                <button
                  className="scroll-button scroll-left"
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className="scroll-button scroll-right"
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              
              <div
                ref={tableContainerRef}
                className="table-container"
                onScroll={handleScroll}
              >
                <table className="error-reports-table">
                  <thead>
                    <tr>
                      {visibleColumns.status && (
                        <th>Durum</th>
                      )}

                      {visibleColumns.errorSummary && (
                        <th>
                          {showSearch.errorSummary ? (
                            <Form.Control
                              type="text"
                              placeholder="Ara..."
                              value={searchTerms.errorSummary}
                              onChange={(e) => handleSearchChange(e, 'errorSummary')}
                              className="search-input"
                            />
                          ) : (
                            <>
                              Hata Açıklaması
                              <FaSearch
                                className="search-icon"
                                onClick={() => toggleSearch('errorSummary')}
                              />
                            </>
                          )}
                        </th>
                      )}

                      {visibleColumns.screenshotUrl && (
                        <th>Ekran Görüntüsü</th>
                      )}

                      {visibleColumns.timestamp && (
                        <th>
                          Tarih
                          {sortOrder === 'asc' ? (
                            <FaSortUp className="sort-icon" onClick={toggleSortOrder} />
                          ) : (
                            <FaSortDown className="sort-icon" onClick={toggleSortOrder} />
                          )}
                        </th>
                      )}

                      {visibleColumns.uid && (
                        <th>
                          {showSearch.uid ? (
                            <Form.Control
                              type="text"
                              placeholder="Ara..."
                              value={searchTerms.uid}
                              onChange={(e) => handleSearchChange(e, 'uid')}
                              className="search-input"
                            />
                          ) : (
                            <>
                              Kullanıcı ID
                              <FaSearch
                                className="search-icon"
                                onClick={() => toggleSearch('uid')}
                              />
                            </>
                          )}
                        </th>
                      )}

                      {visibleColumns.userName && (
                        <th>
                          {showSearch.userName ? (
                            <Form.Control
                              type="text"
                              placeholder="Ara..."
                              value={searchTerms.userName}
                              onChange={(e) => handleSearchChange(e, 'userName')}
                              className="search-input"
                            />
                          ) : (
                            <>
                              Kullanıcı Adı
                              <FaSearch
                                className="search-icon"
                                onClick={() => toggleSearch('userName')}
                              />
                            </>
                          )}
                        </th>
                      )}

                      {visibleColumns.deviceInfo && (
                        <th>Cihaz Bilgisi</th>
                      )}
                      
                      {visibleColumns.browserInfo && (
                        <th>Tarayıcı Bilgisi</th>
                      )}
                      
                      {visibleColumns.errorDetails && (
                        <th>Hata Detayları</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReports.length > 0 ? (
                      filteredReports.map((report, index) => (
                        <tr
                          key={report.id}
                          onClick={() => handleSelectReport(report)}
                          className={selectedErrorReport && selectedErrorReport.id === report.id ? "selected-row" : ""}
                        >
                          {visibleColumns.status && (
                            <td>
                              <div className="status-cell">
                                <select
                                  className={`status-dropdown ${getStatusClass(report.status)}`}
                                  value={report.status}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(report.id, e.target.value);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <option value="Beklemede">Beklemede</option>
                                  <option value="İşlemde">İşlemde</option>
                                  <option value="Tamamlandı">Tamamlandı</option>
                                  <option value="İptal">İptal</option>
                                </select>
                                {report.statusUpdatedByName && (
                                  <div className="status-info" title={`Güncelleyen: ${report.statusUpdatedByName}\nTarih: ${new Date(report.statusUpdatedAt).toLocaleString()}`}>
                                    {getStatusIcon(report.status)}
                                    <span className="status-updated-by">{report.statusUpdatedByName}</span>
                                  </div>
                                )}
                              </div>
                            </td>
                          )}

                          {visibleColumns.errorSummary && (
                            <td>{report.errorSummary || 'No Summary'}</td>
                          )}

                          {visibleColumns.screenshotUrl && (
                            <td>
                              {report.screenshotUrl ? (
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewScreenshot(report.screenshotUrl);
                                  }}
                                  className="view-screenshot-btn"
                                >
                                  Görüntüle
                                </Button>
                              ) : (
                                'Yok'
                              )}
                            </td>
                          )}

                          {visibleColumns.timestamp && (
                            <td>{new Date(report.timestamp).toLocaleString()}</td>
                          )}

                          {visibleColumns.uid && (
                            <td>{report.uid}</td>
                          )}

                          {visibleColumns.userName && (
                            <td>{report.userName}</td>
                          )}

                          {visibleColumns.deviceInfo && (
                            <td>{report.deviceInfo}</td>
                          )}
                          
                          {visibleColumns.browserInfo && (
                            <td>{report.browserInfo}</td>
                          )}
                          
                          {visibleColumns.errorDetails && (
                            <td>{report.errorDetails}</td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="no-data-cell">
                          <div className="no-data">
                            <FaExclamationTriangle className="no-data-icon" />
                            <p>Filtrelere uygun hata raporu bulunamadı</p>
                            {(generalSearchTerm || Object.values(searchTerms).some(term => term !== '')) && (
                              <button className="clear-filters-button" onClick={clearAllFilters}>
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Screenshot Modal */}
      <Modal show={modalShow} onHide={() => setModalShow(false)} size="lg" centered className="screenshot-modal">
        <Modal.Header closeButton>
          <Modal.Title>Ekran Görüntüsü</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedScreenshot ? (
            <img
              src={selectedScreenshot}
              alt="Screenshot"
              className="screenshot-image"
            />
          ) : (
            <p>Ekran görüntüsü bulunamadı.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModalShow(false)}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Error Details Modal */}
      <Modal show={detailModalShow} onHide={() => setDetailModalShow(false)} size="lg" centered className="details-modal">
        <Modal.Header closeButton>
          <Modal.Title>Hata Detayları</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedErrorReport && (
            <div className="error-details">
              <div className="detail-group">
                <h5>Hata Açıklaması</h5>
                <p>{selectedErrorReport.errorSummary || 'No summary provided'}</p>
              </div>
              
              <div className="detail-group">
                <h5>Kullanıcı Bilgileri</h5>
                <p><strong>Adı:</strong> {selectedErrorReport.userName}</p>
                <p><strong>ID:</strong> {selectedErrorReport.uid}</p>
              </div>
              
              <div className="detail-group">
                <h5>Tarih</h5>
                <p>{new Date(selectedErrorReport.timestamp).toLocaleString()}</p>
              </div>

              <div className="detail-group">
                <h5>Durum</h5>
                <div className={`status-badge ${getStatusClass(selectedErrorReport.status)}`}>
                  {getStatusIcon(selectedErrorReport.status)}
                  <span>{selectedErrorReport.status}</span>
                </div>
                {selectedErrorReport.statusUpdatedByName && (
                  <div className="status-update-info">
                    <p><strong>Güncelleyen:</strong> {selectedErrorReport.statusUpdatedByName}</p>
                    <p><strong>Güncelleme Tarihi:</strong> {new Date(selectedErrorReport.statusUpdatedAt).toLocaleString()}</p>
                  </div>
                )}
              </div>

              <div className="detail-group">
                <h5>Sistem Bilgileri</h5>
                <p><strong>Cihaz:</strong> {selectedErrorReport.deviceInfo}</p>
                <p><strong>Tarayıcı:</strong> {selectedErrorReport.browserInfo}</p>
              </div>
              
              <div className="detail-group">
                <h5>Detaylı Hata Bilgisi</h5>
                <pre className="error-details-text">{selectedErrorReport.errorDetails}</pre>
              </div>
              
              {selectedErrorReport.screenshotUrl && (
                <div className="detail-group">
                  <h5>Ekran Görüntüsü</h5>
                  <div className="thumbnail-container">
                    <img 
                      src={selectedErrorReport.screenshotUrl} 
                      alt="Error Screenshot" 
                      className="error-thumbnail"
                      onClick={() => {
                        setDetailModalShow(false);
                        handleViewScreenshot(selectedErrorReport.screenshotUrl);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDetailModalShow(false)}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />
    </div>
  );
};

export default ErrorReportsPage;
